import { cn } from "shared/lib";

import { useMediaQuery } from "shared/hooks";

import { Separator } from "shared/components";

export function EulaPage() {
  const md = useMediaQuery({
    query: "(min-width: 768px)",
  });

  return (
    <div
      className={cn(
        "flex grow flex-col overflow-y-auto px-5 py-5 md:pt-2",
        !md ? "bg-interfaceBgLightPaper" : "bg-interfaceBgDarkPaper",
      )}
    >
      <div
        className={cn(
          "flex grow flex-col items-center rounded-xl",
          md ? "bg-interfaceBgLightPaper" : "",
        )}
      >
        <div className="text-textPrimary mt-4 mb-4 flex w-[700px] max-w-[700px] flex-col gap-4 md:mt-10 md:mb-14 md:gap-10">
          <div className="flex flex-col gap-0.5">
            <h1 className="text-headingxl">End User Agreement</h1>
            <p className="text-bodysm text-textSubdued">
              Last updated 06/12/2024
            </p>
          </div>

          <div className="flex flex-col gap-3">
            <h2 className="text-headinglg">About these terms</h2>
            <ol className="text-bodymd flex list-outside list-decimal flex-col gap-1 pl-6">
              <li>
                This end user agreement ("<b>EUA</b>") is a binding agreement
                between Siena Planning Pty Ltd ("<b>myLot</b>") and any person
                (and/or any entity you are authorised to represent) ("<b>You</b>
                ") who uses or accesses the public-facing web-based planning
                enquiry tool, and associated content and output, that is
                provided to You by myLot via Your local council's website ("
                <b>Tool</b>"). myLot is the creator of the Tool.
              </li>
              <li>
                myLot will make the Tool accessible to You, in accordance with
                this EUA.
              </li>
              <li>
                This EUA sets out Your legal rights and obligations with respect
                to the Tool, so please read and make sure You understand
                everything.
              </li>
            </ol>
          </div>

          <div className="flex flex-col gap-3">
            <h2 className="text-headinglg">Intellectual property and data</h2>
            <ol
              className="text-bodymd flex list-outside list-decimal flex-col gap-1 pl-6"
              start={4}
            >
              <li>
                You acknowledge that, as between myLot and You, myLot owns all
                rights (including intellectual property rights) and title in and
                to the Tool, and You will not dispute the same.
              </li>
              <li>
                When You enter any information into the Tool, You grant myLot a
                licence to use that information so that myLot can use, copy,
                transmit, disclose, distribute, store, analyse and back it up,
                in connection with Your use of the Tool, to enable myLot to
                improve the Tool and provide new services, and communicate with
                You and Your local council.
              </li>
              <li>
                myLot may create anonymised statistical or usage data from Your
                information and the way in which You use the Tool. This
                information belongs to myLot and myLot may use it for its own
                purposes. myLot will never share your email or physical address
                with any party other than the relevant local council that your
                enquiry relates to. For further information on the ways in which
                myLot uses Your information, please refer to the{" "}
                <a
                  className="text-textLink cursor-pointer underline"
                  href="/privacy-policy"
                  rel="noreferrer"
                  tabIndex={0}
                  target="_blank"
                >
                  myLot privacy policy
                </a>
                .
              </li>
            </ol>
          </div>

          <div className="flex flex-col gap-3">
            <h2 className="text-headinglg">What You can and cannot do</h2>
            <ol
              className="text-bodymd flex list-outside list-decimal flex-col gap-1 pl-6"
              start={7}
            >
              <li>
                You may use the Tool for pre-lodgement planning enquiries.
              </li>
              <li>
                You must:
                <ol className="text-bodymd list-lower-alpha flex list-outside flex-col gap-1 pl-6">
                  <li>
                    so that you can get the best from the Tool, provide accurate
                    and complete information;
                  </li>
                  <li>
                    protect Your username and password against misuse or
                    unauthorised access (and tell myLot immediately if You
                    become aware of this); and
                  </li>
                  <li>
                    not:
                    <ol className="text-bodymd list-lower-roman flex list-outside flex-col gap-1 pl-6">
                      <li>
                        copy, distribute, modify, decompile or make derivative
                        works of, myLot's content or the Tool;
                      </li>
                      <li>
                        introduce or upload anything to the Tool that includes a
                        virus or other harmful code;
                      </li>
                      <li>
                        re-sell or sub-licence the Tool or its content or
                        output;
                      </li>
                      <li>do anything illegal via the Tool;</li>
                      <li>
                        act in a manner that is abusive or disrespectful in
                        connection with Your use of the Tool;
                      </li>
                      <li>
                        use the Tool for existing application enquiries (the
                        tool was not designed for this purpose);
                      </li>
                      <li>
                        use the Tool for a commercial purpose, or re-sell or
                        on-provide the Tool;
                      </li>
                      <li>
                        enter or provide to myLot (through the Tool or
                        otherwise) any private, sensitive, confidential, or
                        personal information except to the minimum extent
                        necessary in order to effectively use the Tool and as
                        directed by the Tool.
                      </li>
                    </ol>
                  </li>
                </ol>
              </li>
            </ol>
          </div>

          <div className="flex flex-col gap-3">
            <h2 className="text-headinglg">Disclaimers and liability</h2>
            <ol
              className="text-bodymd flex list-outside list-decimal flex-col gap-1 pl-6"
              start={9}
            >
              <li>
                To the maximum extent permitted by law, and without prejudice to
                any statutory rights You may have, You acknowledge that:
                <ol className="text-bodymd list-lower-alpha flex list-outside flex-col gap-1 pl-6">
                  <li>the Tool is provided 'as is' and 'as available';</li>
                  <li>
                    there are a variety of factors and variables that the Tool
                    does not take into account to provide output;
                  </li>
                  <li>
                    myLot (through the Tool or otherwise) does not provide
                    professional advice;
                  </li>
                  <li>
                    myLot does not provide any warranties, express or implied,
                    in relation the Tool, its output, or its functionality;
                  </li>
                  <li>
                    the output from the Tool does not represent a final planning
                    decision or a permit;
                  </li>
                  <li>
                    the Tool, and the output from the Tool, are a guide only,
                    and you should not rely on it or make any decision based on
                    it; and
                  </li>
                  <li>
                    myLot's liability to You is limited to re-supply of the Tool
                    to You.
                  </li>
                </ol>
              </li>
            </ol>
          </div>

          <div className="flex flex-col gap-3">
            <h2 className="text-headinglg">Fees</h2>
            <ol
              className="text-bodymd flex list-outside list-decimal flex-col gap-1 pl-6"
              start={10}
            >
              <li>myLot provides the Tool to You free of charge.</li>
            </ol>
          </div>

          <div className="flex flex-col gap-3">
            <h2 className="text-headinglg">Other terms</h2>
            <ol
              className="text-bodymd flex list-outside list-decimal flex-col gap-1 pl-6"
              start={11}
            >
              <li>
                myLot may terminate Your access to the Tool at any time if it
                reasonably believes that You have breached this EUA. You may
                stop using the Tool at any time.
              </li>
              <li>
                myLot may update this EUA from time to time by notice to You.
                myLot may update the functionality of the Tool at any time.
              </li>
              <li>
                If You have any dispute or concern regarding this EUA or the
                Tool, You must, before making any public announcement or
                statement, notify myLot and provide myLot with a reasonable
                opportunity to help You and resolve the dispute or concern.
              </li>
            </ol>
          </div>

          <Separator orientation="horizontal" />

          <div className="flex flex-col gap-3">
            <h2 className="text-headinglg">Contact details</h2>
            <ul className="text-bodymd flex list-outside list-disc flex-col gap-1 pl-6">
              <li>
                Email:{" "}
                <a
                  className="text-textLink cursor-pointer underline"
                  href="mailto:help@mylot.ai"
                  tabIndex={0}
                >
                  help@mylot.ai
                </a>
              </li>
              <li>
                Website:{" "}
                <a
                  className="text-textLink cursor-pointer underline"
                  href="https://mylot.ai"
                  rel="noreferrer"
                  target="_blank"
                  tabIndex={0}
                >
                  https://mylot.ai
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EulaPage;
